import type { ReactElement, ReactNode, RefObject } from "react"
import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import CloseIcon from "@mui/icons-material/Close"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import { Grid, IconButton } from "@mui/material"
import { Button, InputText, Typography } from "@synapse-analytics/synapse-ui"
import type { OnChangeFn, VisibilityState } from "@tanstack/react-table"
import {
  type MRT_ColumnDef,
  type MRT_ColumnOrderState,
  type MRT_PaginationState,
  type MRT_Row,
  type MRT_RowSelectionState,
  MaterialReactTable,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
} from "material-react-table"
import { MRT_Localization_EN } from "material-react-table/locales/en"
import { MRT_Localization_ES } from "material-react-table/locales/es"

import { getTheme } from "../../hooks/UseTheme"
import type { TableRefState, VisibilityMap } from "../../types/custom/tables"
import { TableConfigurationRequest } from "../../types/generated/api/TableConfigurationRequest"
import { KonanPagination } from "./KonanPagination"
import { tableIcons } from "./TableIcons"
import {
  getBottomToolbarProps,
  getTableBodyCellProps,
  getTableBodyRowProps,
  getTableHeadCellProps,
  getTableHeadRowProps,
  getTableProps,
  getTopToolbarProps,
} from "./helpers"

const MemoizedCustomPagination = React.memo(KonanPagination)

interface Props {
  id?: string
  title: string | ReactElement
  tooltip?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[]
  isLoading: boolean
  columns: MRT_ColumnDef[]
  enablePagination?: boolean
  manualPagination?: boolean
  enableGlobalFilter?: boolean
  enableHiding?: boolean
  enableFiltering?: boolean
  enableFilters?: boolean
  enableTableHead?: boolean
  detailPanel?: (row: MRT_Row) => ReactElement
  pagination?: MRT_PaginationState
  setPagination?: React.Dispatch<React.SetStateAction<MRT_PaginationState>>
  rowCount?: number
  pageSize?: number
  hiddenColumns?: string[]
  disableToolbar?: boolean
  disableToolbarActions?: boolean
  enableOrdering?: boolean
  layoutMode?: "grid" | "semantic"
  clearAllFiltersFn?: () => void
  source?: TableConfigurationRequest.table
  columnsOrder?: string[]
  onColumnOrderChange?: OnChangeFn<MRT_ColumnOrderState>
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>
  shouldShowProgressBar?: boolean
  columnsVisibility?: VisibilityMap
  tableRef?: RefObject<TableRefState>
  onExportClick?: () => void
  actions?: ReactNode[]
  showColumnFilters?: boolean
  filterInputBoxPlaceHolder?: string
  disablePagination?: boolean
  enableSelectAll?: boolean
  enableRowSelection?: boolean | ((row: MRT_Row<{}>) => boolean)
  getRowId?: (originalRow: {}, index: number, parentRow: MRT_Row) => string
  onRowSelectionChange?: OnChangeFn<MRT_RowSelectionState>
  rowSelection?: {}
  hideRowCount?: boolean
  newStyling?: boolean
  onRowClick?: (row: MRT_Row<{}>) => void
}

export function BaseTable(props: Readonly<Props>): React.ReactElement {
  const { t, i18n } = useTranslation("tables")
  const {
    id,
    title,
    data,
    isLoading,
    columns,
    enablePagination = true,
    manualPagination = false,
    enableGlobalFilter = true,
    enableHiding = true,
    enableFiltering = true,
    enableFilters = true,
    enableTableHead = true,
    enableOrdering = true,
    detailPanel,
    pagination = undefined,
    setPagination,
    disableToolbar = false,
    pageSize = 10,
    rowCount,
    layoutMode = "semantic",
    clearAllFiltersFn,
    source,
    columnsOrder,
    onColumnOrderChange,
    onColumnVisibilityChange,
    shouldShowProgressBar,
    columnsVisibility,
    tableRef,
    onExportClick,
    actions,
    showColumnFilters,
    filterInputBoxPlaceHolder = t("baseTable.filterPlaceholder"),
    disablePagination = false,
    enableRowSelection = false,
    enableSelectAll = false,
    getRowId,
    onRowSelectionChange,
    rowSelection = {},
    hideRowCount = false,
    newStyling,
  } = props

  const theme = getTheme()

  // Using Key to re-render the component in case we need to remove all filter
  // Since material-table-react don't expose an out of the box way to do so
  const [key, setKey] = useState<string>("key")

  // To handle internal pagination in case no pagination state and setter were passed
  const [internalPagination, setInternalPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  })

  const adjustedColumns: MRT_ColumnDef[] = useMemo(() => {
    return columns.map((column) => {
      return {
        Filter: (filterProps) => {
          const value = filterProps.column.getFilterValue() as string

          const id = filterProps.column.id

          return (
            <InputText
              hideDescription
              id={`filter-${id}`}
              key={`filter-${id}`}
              placeholder={filterInputBoxPlaceHolder}
              fullWidth
              value={value}
              handleChange={(e) => {
                filterProps.column.setFilterValue(e.target.value)
              }}
              endAdornment={
                !["", null, undefined].includes(value) ? (
                  <CloseIcon
                    fontSize="small"
                    onClick={() => {
                      filterProps.column.setFilterValue("")
                      ;(document.getElementById(`filter-${id}`) as HTMLInputElement).value = ""
                    }}
                  />
                ) : undefined
              }
            />
          )
        },
        size: 100,
        ...column,
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns])

  /**
   * currently, there's no direct way to control the internal toolbar action buttons, like show/hide all
   * so this mutationObserver removes the 2 buttons, by selecting the classes of their elements
   * using mutationObserver will make sure these buttons will be removed across renders and
   * whenever menu open/close.
   * TODO: remove all this logic and implement a custom action for the toolbar.
   */
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length) {
          const buttons = document.querySelectorAll(".MuiModal-root > .MuiPaper-root > .MuiMenu-list > div > button")
          buttons.forEach((button) => {
            // Check the inner text to identify "Show All" and "Hide All"
            if (
              button.innerHTML.includes(t("baseTable.actions.columnVisibility.hideAll")) ||
              button.innerHTML.includes(t("baseTable.actions.columnVisibility.showAll"))
            ) {
              button.className = "removeMuiTableButton" // Hide "Hide/Show All" button
            }
          })
        }
      })
    })

    // Start observing
    const config = { childList: true, subtree: true }
    const targetNode = document.body // Adjust if needed to a more specific part of your DOM
    observer.observe(targetNode, config)

    // Disconnect the observer when the component unmounts to prevent memory leaks
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const allColumnsHidden = columnsVisibility && Object.values(columnsVisibility).every((isVisible) => !isVisible)

  return (
    <div className={`table table-no-box-shadow ${newStyling && "new-table-style"}`} id={id} key={key}>
      <MaterialReactTable
        localization={i18n.resolvedLanguage === "en" ? MRT_Localization_EN : MRT_Localization_ES}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tableInstanceRef={tableRef as React.MutableRefObject<any>}
        columns={adjustedColumns}
        data={!allColumnsHidden ? (data ?? []) : []}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        icons={{ ...tableIcons, DragHandleIcon: (props: any) => <DragIndicatorIcon {...props} fontSize="small" /> }}
        autoResetPageIndex={false}
        autoResetAll={false}
        enableSorting={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enablePagination={enablePagination}
        enableBottomToolbar={!disablePagination}
        enableTopToolbar={!disableToolbar}
        enableColumnActions={false}
        enableGlobalFilter={enableGlobalFilter}
        enableHiding={enableHiding}
        enableFilters={enableFilters}
        manualPagination={manualPagination}
        onColumnOrderChange={onColumnOrderChange}
        onColumnVisibilityChange={onColumnVisibilityChange}
        enableColumnResizing={false}
        enableColumnOrdering={enableOrdering}
        enableColumnDragging={enableOrdering}
        enableTableHead={enableTableHead}
        enableToolbarInternalActions={false}
        layoutMode={layoutMode}
        enableSelectAll={enableSelectAll}
        enableRowSelection={enableRowSelection}
        getRowId={getRowId}
        onRowSelectionChange={onRowSelectionChange}
        muiSearchTextFieldProps={{
          placeholder: t("baseTable.globalSearchPlaceholder"),
          sx: { minWidth: "8rem" },
          variant: "standard",
        }}
        positionGlobalFilter="none"
        initialState={{ showColumnFilters: showColumnFilters }}
        state={{
          rowSelection: rowSelection ?? undefined,
          showProgressBars: isLoading || shouldShowProgressBar,
          density: "compact",
          pagination: pagination ?? internalPagination,
          columnVisibility: columnsVisibility,
          columnOrder:
            source && Object.values(TableConfigurationRequest.table).includes(source)
              ? ([
                  "mrt-row-expand",
                  ...(enableRowSelection ? ["mrt-row-select"] : []),
                  ...(columnsOrder ?? []),
                ] as string[])
              : ([...(columnsOrder ?? [])] as string[]),
        }}
        onPaginationChange={setPagination ?? setInternalPagination}
        rowCount={rowCount}
        renderEmptyRowsFallback={() => (
          <Grid
            container
            display="flex"
            flexDirection="column"
            height={"200px"}
            justifyContent={"center"}
            alignContent={"center"}
          >
            {allColumnsHidden && !isLoading ? (
              <Fragment>
                <Grid item>
                  <Typography variant="h3-bold" align="center">
                    {t("baseTable.emptyState.allColumnsHidden")}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="p" align="center">
                    {t("baseTable.emptyState.revealColumnMessage")}
                  </Typography>
                </Grid>
              </Fragment>
            ) : (
              <Grid item>
                <Typography variant="h3-regular" align="center">
                  {!isLoading ? t("baseTable.emptyState.noDataMessage") : t("baseTable.emptyState.fetchingMessage")}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
        renderDetailPanel={detailPanel ? ({ row }) => detailPanel(row) : undefined}
        renderTopToolbarCustomActions={({ table }) => {
          const tableState = table.getState()
          return (
            // hardcoding height to align text with icons
            <Grid container item xs direction="row" justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h3-bold">{title}</Typography>
              </Grid>

              <Grid container item xs justifyContent={"flex-end"} alignItems="center" textAlign={"right"} gap={1}>
                {(tableState.columnFilters.length > 0 || clearAllFiltersFn) && (
                  <IconButton
                    size="medium"
                    onClick={() => {
                      clearAllFiltersFn?.()
                      setKey(key + 1)
                    }}
                  >
                    <FilterAltOffIcon fontSize="medium" style={{ color: theme.palette.base.text[2] }} />
                  </IconButton>
                )}

                {enableFiltering && <ToggleFiltersButton table={table} />}

                {enableHiding && <ShowHideColumnsButton table={table} />}

                {onExportClick && (
                  <Button
                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: "20px" }} />}
                    variant="secondary"
                    onClick={() => onExportClick?.()}
                    size="regular"
                  >
                    {t("baseTable.actions.export")}
                  </Button>
                )}

                {actions?.map((action, index) => {
                  return (
                    <Grid item key={index}>
                      {action}
                    </Grid>
                  )
                })}
              </Grid>
            </Grid>
          )
        }}
        // TODO:: fix chipping at the edges
        muiTableProps={getTableProps(newStyling, disableToolbar, data?.length === 0)}
        // overwriting Top toolbar styles
        muiTopToolbarProps={getTopToolbarProps(newStyling)}
        // overwriting Bottom toolbar styles
        muiBottomToolbarProps={getBottomToolbarProps(newStyling)}
        // overwriting expand all btn styles
        muiExpandAllButtonProps={{
          sx: {
            display: "none",
            width: "20px",
          },
        }}
        // overwriting HEADER cell styles
        muiTableHeadCellProps={getTableHeadCellProps(newStyling, disableToolbar)}
        // overwriting HEADER row styles
        muiTableHeadRowProps={getTableHeadRowProps(newStyling, disableToolbar)}
        muiTableBodyProps={{ sx: { position: "static" } }}
        // overwriting BODY row styles
        muiTableBodyRowProps={({ row }) => {
          const defaultProps = getTableBodyRowProps(newStyling)

          if (!props.onRowClick) {
            return defaultProps
          }

          return {
            ...defaultProps,
            onClick: () => {
              props.onRowClick?.(row)
            },
            sx: {
              ...defaultProps.sx,
              cursor: "pointer",
              "&:hover": { backgroundColor: "var(--base-background-1)" },
            },
          }
        }}
        // overwriting BODY cell styles
        muiTableBodyCellProps={getTableBodyCellProps(newStyling)}
        // Custom pagination component
        muiTablePaginationProps={(props) => {
          return {
            component: () => {
              return (
                <MemoizedCustomPagination
                  count={rowCount ?? data.length ?? 0}
                  page={props.table.getState().pagination.pageIndex}
                  onPageChange={(index: number) => props.table.setPageIndex(index)}
                  rowsPerPage={props.table.getState().pagination.pageSize}
                  setRowsPerPage={(pageSize: number) => props.table.setPageSize(pageSize)}
                  hideRowCount={hideRowCount}
                />
              )
            },
          }
        }}
      />
    </div>
  )
}
